// src/features/auth/ui/pages/PatreonAuthCallback.tsx
import React, { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Typography,
  Button,
  Alert
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as patreonApi from '../../../../api/patreon';
import { theme } from '../../../../theme';
import axios from 'axios';
import { authBus } from '../../domain/authBus';
import { getNextFromQuery, popNextUrl } from '../../domain/authNavigation';

function getAxiosError(err: unknown): { status?: number; message?: string } {
  if (!axios.isAxiosError(err)) {
    return { message: 'A server error occurred. Please try again later.' };
  }

  const status = err.response?.status;

  const message =
    err.response?.data?.error ||
    err.response?.data?.message ||
    err.response?.statusText ||
    'A server error occurred. Please try again later.';

  return { status, message };
}

const PatreonAuthCallback: React.FC = () => {
  const [search] = useSearchParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    const code = search.get('code');
    const state = search.get('state');

    if (!code || !state) {
      setLoading(false);
      setErrorMsg('Authentication parameters are missing.');
      return;
    }

    const run = async () => {
      try {
        await patreonApi.patreonCallback(code, state);
        authBus.emit('login');

        const next = getNextFromQuery(search) ?? popNextUrl() ?? '/';

        navigate(next, { replace: true });
      } catch (err) {
        const { message } = getAxiosError(err);
        setErrorMsg(message || 'Failed to complete Patreon authentication.');
      } finally {
        setLoading(false);
      }
    };

    void run();
  }, [search, navigate]);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '70vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: theme.palette.background.default,
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>
            Connecting to Patreon…
          </Typography>
        </Box>
      </Box>
    );
  }

  if (errorMsg) {
    return (
      <Box
        sx={{
          minHeight: '70vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          px: 2,
          textAlign: 'center',
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 560, width: '100%' }}>
          {errorMsg}
        </Alert>

        <Button
          variant="contained"
          onClick={() => navigate('/', { replace: true })}
          sx={{ minWidth: 160 }}
        >
          Back
        </Button>
        <Button
          variant="outlined"
          onClick={() => navigate('/login', { replace: true })}
          sx={{ minWidth: 160 }}
        >
          Sign in options
        </Button>
      </Box>
    );
  }

  return null;
};

export default PatreonAuthCallback;
